/**
 * Spanish, and the shape every other language must satisfy.
 *
 * This file is the source of truth twice over: it holds the copy the game
 * shipped with, and `typeof es` becomes the type each translation is checked
 * against — so a missing key is a compile error rather than a blank label
 * somebody notices in production.
 *
 * Rules for anything added here:
 *
 *  - Strings are authored uppercase because the face is uppercase-only and
 *    everything renders through `text-transform: uppercase`. `aria-label`s are
 *    the exception: they are spoken, never drawn, so they stay sentence case.
 *  - Only glyphs in the font subset (`scripts/glyphs.mjs`). A test enforces it.
 *  - Anything with a number or a name is a function, not a template assembled
 *    at the call site: word order is not the same in every language, and
 *    concatenation at the call site is what makes that impossible to fix.
 */

export const es = {
  common: {
    loading: 'CARGANDO...',
    offline: 'SIN CONEXION',
    couldNotLoad: 'NO SE PUDO CARGAR',
    retry: 'REINTENTAR',
    cancel: 'CANCELAR',
    back: 'VOLVER',
    player: 'PLAYER',
    guest: 'INVITADO',
    points: 'PUNTOS',
    moves: 'MOVIMIENTOS',
    maxTile: 'FICHA MAX',
    maxCombo: 'COMBO MAX',
    matches: 'PARTIDAS',
    score: 'SCORE',
    date: 'FECHA',
    rank: 'RANK',
    position: 'POS',
    name: 'NAME',
    playerColumn: 'JUGADOR',
    winsLosses: 'V/D',
    max: 'MAX',
    modesMenu: 'MENU DE MODOS',
    combo: (n: number) => `COMBO x${n}`,
    versusName: (name: string) => `VS ${name}`,
    // Spoken, not drawn.
    ariaBack: 'Volver',
    ariaClose: 'Cerrar aviso',
    ariaNotifications: 'Notificaciones',
  },

  menu: {
    subtitle: 'BATTLE',
    play: 'JUGAR',
    rankings: 'RANKINGS',
    history: 'HISTORIAL',
    settings: 'CONFIGURACION',
    online: 'EN LINEA',
    offline: 'SIN CONEXION',
  },

  play: {
    singlePlayer: '◆ UN JUGADOR',
    multiplayer: '◆ MULTIPLAYER',
    modes: {
      matchTitle: 'BUSCAR MATCH',
      matchHint: 'RIVAL ONLINE DE TU NIVEL',
      botTitle: 'VS BOT',
      botHint: 'PARTIDA INMEDIATA',
      privateTitle: 'PARTIDA PRIVADA',
      privateHint: 'CREA O ENTRA CON CODIGO',
      timedTitle: '2 MINUTOS',
      timedHint: 'MAXIMO SCORE EN 2:00',
      endlessTitle: 'SIN LIMITE',
      endlessHint: 'RECORD PERSONAL · NO COMPITE',
    },
    chooseDifficulty: '◆ ELIGE RIVAL',
    difficulties: {
      easy: { title: 'FACIL', hint: 'LENTO Y DESPISTADO' },
      medium: { title: 'MEDIO', hint: 'JUEGA BIEN · FALLA A VECES' },
      hard: { title: 'DIFICIL', hint: 'PIENSA DOS JUGADAS' },
      insane: { title: 'DEMENCIAL', hint: 'NUNCA FALLA · RAPIDO' },
    },
    difficultyFootnote: 'SIN RIVAL ONLINE, LA CPU ENTRA SOLA',
    privateIntro: 'CREA UNA SALA Y COMPARTE EL CODIGO, O ESCRIBE EL DE TU AMIGO.',
    createRoom: 'CREAR SALA',
    enterCode: 'ENTRAR CON CODIGO',
    codePrompt: 'ESCRIBE EL CODIGO DE TU AMIGO',
    codeIncomplete: 'COMPLETA EL CODIGO PARA CONTINUAR',
    join: 'UNIRSE',
    personalBest: (score: string) => `TU RECORD: ${score}`,
  },

  matchmaking: {
    searching: 'BUSCANDO',
    opponent: 'OPONENTE...',
    time: 'TIEMPO',
    yourElo: 'TU ELO',
    unranked: 'SIN CLASIFICAR',
    versus: 'VS',
    autoCancel: 'LA BUSQUEDA SE CANCELA SOLA AL AGOTARSE',
    ariaSearching: 'Buscando rival',
  },

  battle: {
    ready: 'READY',
    steady: 'LISTOS',
    go: 'GO!',
    next: 'NEXT',
    combo: 'COMBO',
    round: 'ROUND 01',
    swipe: 'SWIPE',
    result: 'RESULTADO',
    victory: 'VICTORIA',
    defeat: 'DERROTA',
    draw: 'EMPATE',
    rematch: 'REVANCHA',
    powerActivated: 'PODER ACTIVADO',
    reconnecting: 'RECONECTANDO...',
    keepingYourGame: 'CONSERVANDO TU PARTIDA',
    preparingBoard: 'PREPARANDO TABLERO...',
    preparingBot: 'PREPARANDO BOT...',
    waitingOpponent: 'ESPERANDO RIVAL...',
    searchingOpponent: 'BUSCANDO OPONENTE...',
    waiting: 'ESPERANDO',
    shareCode: 'COMPARTE ESTE CODIGO',
    tapInvite: 'TOCA INVITAR PARA COMPARTIR',
    invite: 'INVITAR',
    room: 'SALA',
    fifteenSecondWindow: 'VENTANA DE 15 SEGUNDOS',
    connectionRequired: 'SE NECESITA CONEXION',
    gameOver: 'FIN DE PARTIDA',
    offlineNotSaved: 'SIN CONEXION · ESTA PARTIDA NO SE GUARDA',
    playAgain: 'JUGAR DE NUEVO',
    preparing: 'PREPARANDO...',
    time: 'TIEMPO',
    endless: 'SIN LIMITE',
    timed: '2 MINUTOS',
    swipeHint: (combo: number, energy: number) =>
      `DESLIZA O USA LAS FLECHAS\nCOMBO MAX x${combo} · ENERGIA ${energy}`,
    // Panel status labels. Short by necessity: they sit inside the HUD row.
    statusReady: 'LISTO',
    statusFight: 'FIGHT',
    statusAttack: 'ATTACK',
    statusHit: 'HIT!',
    statusKo: 'K.O.',
    statusLobby: 'LOBBY',
    statusWait: 'ESPERA',
    statusOnline: 'ONLINE',
    statusBot: 'BOT',
    statusCpu: 'CPU',
  },

  rankings: {
    title: 'RANKINGS',
    boardElo: 'ELO',
    boardScore: 'SCORE',
    scopeGlobal: 'GLOBAL',
    scopeWeekly: 'SEMANAL',
    scopeDaily: 'HOY',
    versus: 'VERSUS',
    all: 'TODOS',
    worldRecord: 'RECORD MUNDIAL',
    offlineMessage: 'SIN CONEXION\n\nNO SE PUDO CARGAR\nLA TABLA.',
    emptyMessage: 'AUN NO HAY MARCAS\n\nJUEGA UNA PARTIDA\nPARA APARECER EN\nLA TABLA.',
    ariaBoard: 'Tabla',
    ariaScope: 'Periodo',
    weeklyDelta: (delta: string) => `${delta} ESTA SEMANA`,
  },

  history: {
    title: 'HISTORIAL',
    offlineMessage: 'SIN CONEXION\n\nNO SE PUDO CARGAR\nEL HISTORIAL.',
    emptyMessage: 'AUN NO HAY PARTIDAS\n\nJUEGA UNA Y APARECERA\nAQUI.',
    playedSolo: 'EN SOLITARIO',
    outcome: {
      win: 'VICTORIA',
      loss: 'DERROTA',
      draw: 'EMPATE',
      abandoned: 'ABANDONO',
    },
    mode: {
      versus: 'VERSUS',
      bot: 'VS BOT',
      solo: '2 MINUTOS',
      endless: 'SIN LIMITE',
    },
  },

  profile: {
    title: 'PERFIL',
    bestScore: 'TU MEJOR PUNTUACION',
    bestScorePublic: 'MEJOR PUNTUACION',
    versusElo: 'ELO VERSUS',
    matches: 'PARTIDAS',
    winsLosses: 'V / D',
    bestStreak: 'MEJOR RACHA',
    achievements: 'LOGROS',
    viewAchievements: 'VER LOGROS',
    history: 'HISTORIAL',
    highScores: 'HIGH SCORES',
    offlineMessage: 'SIN CONEXION\n\nNO SE PUDO CARGAR\nTU PERFIL.',
    offlineMessagePublic: 'SIN CONEXION\n\nNO SE PUDO CARGAR\nEL PERFIL.',
  },

  highScores: {
    title: 'HIGH SCORES',
    bestScore: 'TU MEJOR PUNTUACION',
    bestScorePublic: 'MEJOR PUNTUACION',
    offlineMessage: 'SIN CONEXION\n\nNO SE PUDIERON CARGAR\nTUS RECORDS.',
    offlineMessagePublic: 'SIN CONEXION\n\nNO SE PUDIERON CARGAR\nLOS RECORDS.',
    emptyMessage: 'AUN NO TIENES\nPARTIDAS EN SOLO.\n\nJUEGA UNA PARA\nABRIR LA TABLA.',
    emptyMessagePublic: 'ESTE JUGADOR AUN NO TIENE\nPARTIDAS EN SOLO.',
  },

  achievements: {
    title: 'LOGROS',
    all: 'TODOS',
    unlocked: 'DESBLOQUEADOS',
    secret: 'LOGRO SECRETO',
    secretHint: 'SIGUE JUGANDO PARA DESCUBRIRLO',
    offline: 'SIN CONEXION · PROGRESO NO DISPONIBLE',
    ariaCategory: 'Categoria',
    category: {
      combate: 'COMBATE',
      score: 'SCORE',
      social: 'SOCIAL',
    },
    rarity: {
      comun: 'COMUN',
      raro: 'RARO',
      epico: 'EPICO',
      legendario: 'LEGENDARIO',
    },
    /**
     * Keyed by achievement id, because the ids are the rules and the words are
     * not. `shared` owns what unlocks; this owns what it is called.
     */
    items: {
      'primera-sangre': { name: 'PRIMERA SANGRE', description: 'GANA TU PRIMERA PARTIDA VERSUS' },
      'ko-tecnico': { name: 'K.O. TÉCNICO', description: 'GANA 10 PARTIDAS POR K.O.' },
      veterano: { name: 'VETERANO', description: 'JUEGA 100 PARTIDAS' },
      verdugo: { name: 'VERDUGO', description: 'GANA 50 PARTIDAS VERSUS' },
      imparable: { name: 'IMPARABLE', description: 'ENCADENA 5 VICTORIAS SEGUIDAS' },
      artillero: { name: 'ARTILLERO', description: 'INFLIGE 100 000 DE DAÑO' },
      'sin-un-rasguno': {
        name: 'SIN UN RASGUÑO',
        description: 'GANA UNA PARTIDA SIN RECIBIR DAÑO',
      },
      'dominio-total': { name: 'DOMINIO TOTAL', description: 'ENCADENA 10 VICTORIAS SEGUIDAS' },
      'leyenda-arena': { name: 'LEYENDA DE LA ARENA', description: 'GANA 250 PARTIDAS VERSUS' },
      aprendiz: { name: 'APRENDIZ', description: 'CONSIGUE UNA FICHA 512' },
      maraton: { name: 'MARATÓN', description: 'JUEGA 50 PARTIDAS EN SOLITARIO' },
      maestro: { name: 'MAESTRO', description: 'CONSIGUE UNA FICHA 1024' },
      'cadena-mortal': { name: 'CADENA MORTAL', description: 'LLEGA A UN COMBO x15' },
      'seis-cifras': { name: 'SEIS CIFRAS', description: 'ALCANZA 100 000 PUNTOS EN UNA PARTIDA' },
      'primer-2048': { name: 'PRIMER 2048', description: 'CONSIGUE TU PRIMERA FICHA 2048' },
      'reaccion-en-cadena': { name: 'REACCIÓN EN CADENA', description: 'LLEGA A UN COMBO x25' },
      'medio-millon': { name: 'MEDIO MILLÓN', description: 'ACUMULA 500 000 PUNTOS' },
      'mas-alla': { name: 'MÁS ALLÁ', description: 'CONSIGUE UNA FICHA 4096' },
      'primer-rival': { name: 'PRIMER RIVAL', description: 'ENFRÉNTATE A OTRO JUGADOR' },
      sociable: { name: 'SOCIABLE', description: 'ENFRÉNTATE A 10 RIVALES DISTINTOS' },
      'rival-fiel': { name: 'RIVAL FIEL', description: 'JUEGA 10 PARTIDAS CONTRA EL MISMO RIVAL' },
      'cara-conocida': { name: 'CARA CONOCIDA', description: 'ENFRÉNTATE A 50 RIVALES DISTINTOS' },
      'david-contra-goliat': {
        name: 'DAVID CONTRA GOLIAT',
        description: 'GANA A UN RIVAL 300 PUNTOS DE ELO POR ENCIMA',
      },
    },
  },

  settings: {
    title: 'CONFIGURACION',
    audio: 'AUDIO',
    sound: 'SONIDO',
    music: 'MUSICA',
    effects: 'EFECTOS',
    vibration: 'VIBRACION',
    game: 'JUEGO',
    language: 'IDIOMA',
    languageName: 'ESPANOL',
    crt: 'EFECTO CRT',
    reducedMotion: 'MENOS ANIMACION',
    reducedMotionHint: 'TU SISTEMA PUEDE FORZARLO',
    save: 'GUARDAR CAMBIOS',
    unsavedTitle: 'CAMBIOS SIN GUARDAR',
    unsavedMessage: 'SI SALES AHORA VOLVERAN\nLOS VALORES ANTERIORES.',
    saveAndExit: 'GUARDAR Y SALIR',
    discardChanges: 'DESCARTAR',
    keepEditing: 'SEGUIR EDITANDO',
    account: 'CUENTA',
    edit: 'EDITAR',
    signIn: 'INICIAR SESION',
    signOut: 'CERRAR SESION',
    signingOut: 'CERRANDO',
    signedInAs: (username: string) => `SESION DE ${username}`,
    progressKept: 'TU PROGRESO SE CONSERVA AL CREAR LA CUENTA',
    footer: '2K48 BATTLE v1.0',
    support: 'SOPORTE · PRIVACIDAD',
  },

  auth: {
    signInTitle: 'ENTRAR',
    signingIn: 'ENTRANDO',
    username: 'USUARIO',
    password: 'CONTRASEÑA',
    email: 'CORREO',
    playerName: 'NOMBRE DE JUGADOR',
    rememberMe: 'RECORDARME',
    forgot: '¿OLVIDASTE?',
    forgotSoon: 'DISPONIBLE PROXIMAMENTE',
    createAccount: 'CREAR CUENTA',
    creating: 'CREANDO',
    playAsGuest: 'JUGAR COMO INVITADO',
    insertCoin: 'INSERT COIN · v1.0',
    registerTitle: 'NUEVO JUGADOR',
    chooseAvatar: 'ELIGE TU AVATAR',
    available: '✓ DISPONIBLE',
    unavailable: '✕ NO DISPONIBLE',
    termsLine1: 'AL CONTINUAR ACEPTAS',
    termsLine2: 'LOS TERMINOS DEL JUEGO',
    charactersLeft: (n: number) => `${n} CARACTERES MAS`,
    ariaAvatar: 'Elige tu avatar',
    ariaStrength: 'Fuerza de la contraseña',
    errors: {
      invalidCredentials: 'USUARIO O CONTRASEÑA INCORRECTOS',
      accountDisabled: 'ESTA CUENTA ESTA DESHABILITADA',
      usernameTaken: 'ESE NOMBRE YA ESTA EN USO',
      emailTaken: 'ESE CORREO YA TIENE CUENTA',
      playerAlreadyClaimed: 'ESTE DISPOSITIVO YA TIENE CUENTA. INICIA SESION',
      invalidUsername: 'NOMBRE NO VALIDO',
      invalidEmail: 'CORREO NO VALIDO',
      invalidPassword: (min: number) => `LA CONTRASEÑA NECESITA ${min} CARACTERES`,
      deviceRequired: 'NO SE PUDO IDENTIFICAR ESTE DISPOSITIVO',
      unauthorized: 'LA SESION YA NO ES VALIDA',
      serverError: 'ERROR DEL SERVIDOR. INTENTA DE NUEVO',
      generic: 'NO SE PUDO COMPLETAR. INTENTA DE NUEVO',
      offline: 'SIN CONEXION CON EL SERVIDOR',
      rateLimitedOneMinute: 'DEMASIADOS INTENTOS. ESPERA UN MINUTO',
      rateLimited: (minutes: number) => `DEMASIADOS INTENTOS. ESPERA ${minutes} MINUTOS`,
      usernameRequired: 'ESCRIBE UN NOMBRE',
      usernameTooShort: (min: number) => `MINIMO ${min} CARACTERES`,
      usernameTooLong: (max: number) => `MAXIMO ${max} CARACTERES`,
      usernameCharset: 'SOLO LETRAS, NUMEROS Y _',
      usernameEdges: 'NO PUEDE EMPEZAR NI TERMINAR EN _',
      emailRequired: 'ESCRIBE TU CORREO',
      emailTooLong: 'CORREO DEMASIADO LARGO',
    },
  },

  toasts: {
    settingsSaved: 'CONFIGURACION GUARDADA',
    signedIn: 'SESION INICIADA',
    signedOut: 'SESION CERRADA',
    accountCreated: 'CUENTA CREADA',
    unstableConnection: 'CONEXION INESTABLE',
    connectionRestored: 'CONEXION RESTABLECIDA',
    matchCreated: 'PARTIDA CREADA CORRECTAMENTE',
    codeExpired: 'EL CODIGO DE PARTIDA EXPIRO',
    codeExpiredDetail: 'EL CODIGO YA NO ES VALIDO O LA SALA SE CERRO.',
    matchUnavailable: 'PARTIDA NO DISPONIBLE',
    couldNotConnect: 'NO SE PUDO CONECTAR',
    couldNotConnectServer: 'NO SE PUDO CONECTAR AL SERVIDOR',
    offlineRun: 'JUGANDO SIN CONEXION · NO SE GUARDA',
    botFilledIn: 'SIN RIVAL ONLINE · ENTRA LA CPU',
    opponentLeft: 'TU OPONENTE ABANDONO LA PARTIDA',
    matchOver: 'PARTIDA TERMINADA',
    opponentFound: 'RIVAL ENCONTRADO',
    achievementsUnlocked: (names: string) => `LOGRO: ${names}`,
  },
};

export type Catalogue = typeof es;
